import axios from 'axios';

// API Base URL - Her seferinde runtime'da tespit et
const getBackendURL = () => {
  // REACT_APP_BACKEND_URL varsa onu kullan, yoksa window.location.origin
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

// ==================== DEVICE ID MANAGEMENT ====================
// Benzersiz cihaz/tarayıcı kimliği oluştur ve sakla
const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    // Benzersiz ID oluştur: timestamp + random + browser fingerprint
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 15);
    const fingerprint = navigator.userAgent.replace(/\D+/g, '').substring(0, 10);
    deviceId = `${timestamp}-${random}-${fingerprint}`;
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

// Session bilgilerini al
const getSessionInfo = () => {
  const sessionId = localStorage.getItem('session_id');
  const deviceId = getDeviceId();
  return { sessionId, deviceId };
};

// ==================== REMEMBERED LOGIN (Silent Re-Auth) ====================
// Session expiry / pod restart / cache clear sonrası kullanıcının atılmaması
// için credentials'i localStorage'da (base64 encoded) tutuyoruz. 401 alındığında
// arka planda yeniden login olup orijinal request'i retry ediyoruz.
// Not: base64 obfuscation güvenlik değil — sadece plain text görünümü önler.
// Kullanıcı isterse Login sayfasındaki "Beni Hatırla" toggle'ı ile kapatabilir.

const REMEMBER_KEY = 'remembered_login_v1';

const saveRememberedLogin = (username, password) => {
  try {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify({ u: username, p: password, ts: Date.now() }))));
    localStorage.setItem(REMEMBER_KEY, encoded);
  } catch (err) {
    console.warn('saveRememberedLogin error:', err);
  }
};

const readRememberedLogin = () => {
  try {
    const encoded = localStorage.getItem(REMEMBER_KEY);
    if (!encoded) return null;
    const obj = JSON.parse(decodeURIComponent(escape(atob(encoded))));
    if (!obj?.u || !obj?.p) return null;
    return obj;
  } catch {
    return null;
  }
};

const clearRememberedLogin = () => {
  try { localStorage.removeItem(REMEMBER_KEY); } catch { /* ignore */ }
};

/**
 * Silent re-auth: /proxy/login endpoint'ini hatırlanan credentials ile çağır,
 * localStorage.user + token'i güncelle. Başarısızsa null döner.
 * Aynı anda birden fazla 401 gelirse tek bir re-auth promise'ini paylaş.
 */
let _reauthPromise = null;
export const silentReauth = () => {
  if (_reauthPromise) return _reauthPromise;
  _reauthPromise = (async () => {
    try {
      const creds = readRememberedLogin();
      if (!creds) return null;
      const deviceId = getDeviceId();
      // apiClient DEĞİL — plain axios ile çağır ki 401 interceptor'ı tekrar tetiklenmesin
      const resp = await axios.post(`${getBackendURL()}/api/proxy/login`, {
        username: creds.u,
        password: creds.p,
        device_id: deviceId,
      }, { timeout: 15000 });
      const apiData = resp.data;
      if (apiData?.token && apiData?.user) {
        // localStorage.user objesini frontend formatında güncelle
        const existing = JSON.parse(localStorage.getItem('user') || '{}');
        const updated = {
          ...existing,
          username: apiData.user.username,
          name: apiData.user.username,
          token: apiData.token,
          companyName: apiData.user.companyName,
          warehouseCode: apiData.user.warehouseCode,
          yetki: apiData.user.yetki || '1',
          storeCode: apiData.user.storeCode || apiData.user.warehouseCode,
          personelKodu: apiData.user.personelKodu || '',
          sessionId: apiData.session_id,
        };
        localStorage.setItem('user', JSON.stringify(updated));
        if (apiData.session_id) localStorage.setItem('session_id', apiData.session_id);
        console.info('[silent-reauth] başarılı — session yenilendi');
        return apiData.token;
      }
      return null;
    } catch (e) {
      console.warn('[silent-reauth] hata:', e?.message || e);
      return null;
    } finally {
      _reauthPromise = null;
    }
  })();
  return _reauthPromise;
};

/**
 * Global fetch wrapper — 401 gelen fetch çağrılarını silent re-auth ile retry eder.
 * SatisFatura ve diğer sayfaların direkt `fetch(url)` kullanımlarında da session
 * kesilmesini önler. Native fetch'i `window.__nativeFetch__` altında saklar.
 *
 * Kural: Sadece REACT_APP_BACKEND_URL ile başlayan istekleri sarmalar (kendi API'miz).
 * Login endpoint'ini kendisini bypass eder (loop önlemek için).
 * `Authorization` header YOKSA da otomatik token ekle (opsiyonel — çoğu fetch çağrısında
 * el ile Authorization eklendiği için buna güvenmiyoruz).
 */
export const installGlobalFetchInterceptor = () => {
  if (typeof window === 'undefined' || window.__fetchInterceptorInstalled__) return;
  const nativeFetch = window.fetch.bind(window);
  window.__nativeFetch__ = nativeFetch;
  window.__fetchInterceptorInstalled__ = true;

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const backendURL = getBackendURL();

    // Sadece kendi backend'imiz — 3rd party fetch'leri sarmalamıyoruz
    const isOurBackend = backendURL && url.startsWith(backendURL);
    // Login endpoint'inin kendisi 401 verdiğinde silent re-auth loop yapmasın
    const isLoginEndpoint = url.includes('/proxy/login') || url.includes('/api/proxy/login');

    if (!isOurBackend || isLoginEndpoint) {
      return nativeFetch(input, init);
    }

    let resp = await nativeFetch(input, init);
    if (resp.status !== 401) return resp;

    // 401 → silent re-auth dene, aynı isteği yeni token ile retry
    const newToken = await silentReauth();
    if (!newToken) {
      // Re-auth başarısız → logout
      console.warn('[fetch-interceptor] Silent re-auth başarısız — logout');
      clearRememberedLogin();
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('session_id');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return resp;
    }

    // Retry: init.headers'ı klonla, Authorization'ı yeni token ile değiştir
    const newInit = { ...init };
    const originalHeaders = init.headers || {};
    let newHeaders;
    if (originalHeaders instanceof Headers) {
      newHeaders = new Headers(originalHeaders);
      newHeaders.set('Authorization', `Bearer ${newToken}`);
    } else if (Array.isArray(originalHeaders)) {
      newHeaders = originalHeaders.filter(([k]) => k.toLowerCase() !== 'authorization');
      newHeaders.push(['Authorization', `Bearer ${newToken}`]);
    } else {
      newHeaders = { ...originalHeaders, Authorization: `Bearer ${newToken}` };
    }
    newInit.headers = newHeaders;
    return nativeFetch(input, newInit);
  };
};

// Install immediately (import edildiğinde global fetch sarmalanır)
installGlobalFetchInterceptor();

// Axios instance oluştur - BASE URL /api olarak
const apiClient = axios.create({
  baseURL: `${getBackendURL()}/api`,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 120000 // 120 saniye timeout
});

// Request interceptor - Her istekte token ekle
apiClient.interceptors.request.use(
  (config) => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.token) {
          config.headers.Authorization = `Bearer ${user.token}`;
        }
      } catch (err) {
        console.error('Error parsing user token:', err);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - 401 durumunda silent re-auth + retry
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const config = error.config || {};
    const url = config.url || '';

    // Pazaryeri (Trendyol/Hepsi/Koçtaş) 401 → logout tetikleme, sadece log
    const isMarketplaceEndpoint = url.includes('/hepsiburada/') ||
                                  url.includes('/trendyol/') ||
                                  url.includes('/koctas/');

    if (status === 401 && isMarketplaceEndpoint) {
      console.warn('Marketplace API 401:', url);
      return Promise.reject(error);
    }

    // Auth endpoint'in KENDİSİNDE 401 (login yanlış) → silent re-auth denemez, logout
    const isLoginEndpoint = url.includes('/proxy/login');
    if (status === 401 && isLoginEndpoint) {
      clearRememberedLogin();
      return Promise.reject(error);
    }

    // Kritik yol: silent re-auth ile retry
    if (status === 401 && !config._retriedAfterReauth) {
      const newToken = await silentReauth();
      if (newToken) {
        // Aynı request'i yeni token ile tekrar dene
        config._retriedAfterReauth = true;
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return apiClient.request(config);
      }
      // Re-auth başarısız → logout
      console.warn('Silent re-auth başarısız — logout yapılıyor');
      stopSessionHeartbeat();
      clearRememberedLogin();
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('session_id');
      // Sadece /login değilse yönlendir
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  /**
   * Kullanıcı girişi (Proxy üzerinden) - Session yönetimi ile
   * @param {string} username
   * @param {string} password
   * @param {object} [opts]
   * @param {boolean} [opts.remember=true] - "Beni Hatırla" — credentials'i sakla, silent re-auth aktif olsun
   */
  login: async (username, password, opts = {}) => {
    const remember = opts.remember !== false; // default true
    try {
      const deviceId = getDeviceId();
      
      const response = await apiClient.post('/proxy/login', {
        username,
        password,
        device_id: deviceId
      });
      
      // API Response formatı: { token, user: {...}, session_id, device_id } veya { error: true, blocked: true, message }
      const apiData = response.data;
      
      // Session limit kontrolü - engellenmiş mi?
      if (apiData.blocked || apiData.error) {
        return {
          success: false,
          blocked: true,
          reason: apiData.reason,
          message: apiData.message || 'Giriş engellendi'
        };
      }
      
      if (apiData.token && apiData.user) {
        // Session bilgilerini kaydet
        if (apiData.session_id) {
          localStorage.setItem('session_id', apiData.session_id);
        }
        
        // Silent re-auth için credentials'i sakla (kullanıcı oturumdan atılmasın)
        if (remember) {
          saveRememberedLogin(username, password);
        } else {
          clearRememberedLogin();
        }
        
        // Heartbeat başlat (5 dakikada bir)
        startSessionHeartbeat();
        
        // Frontend formatına çevir
        return {
          success: true,
          data: {
            username: apiData.user.username,
            name: apiData.user.username,
            role: 'user',
            token: apiData.token,
            companyName: apiData.user.companyName,
            warehouseCode: apiData.user.warehouseCode,
            yetki: apiData.user.yetki || '1',
            storeCode: apiData.user.storeCode || apiData.user.warehouseCode,
            personelKodu: apiData.user.personelKodu || '',
            sessionId: apiData.session_id
          },
          message: 'Login successful'
        };
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (error) {
      // Blocked response'u kontrol et
      if (error.response?.data?.blocked) {
        return {
          success: false,
          blocked: true,
          reason: error.response.data.reason,
          message: error.response.data.message
        };
      }
      throw {
        message: error.response?.data?.detail || error.message || 'Giriş yapılırken bir hata oluştu',
        error: 'LOGIN_ERROR'
      };
    }
  },

  /**
   * QR kod ile giriş (Otomatik)
   */
  qrLogin: async (code) => {
    try {
      const response = await apiClient.get(`/proxy/qr-login/${code}`);
      return response.data;
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'QR kod doğrulanamadı',
        error: 'QR_LOGIN_ERROR'
      };
    }
  },
  
  /**
   * Çıkış yap - Session'ı temizle
   */
  logout: async () => {
    try {
      const { sessionId, deviceId } = getSessionInfo();
      if (sessionId) {
        await apiClient.post('/session/logout', {
          session_id: sessionId,
          device_id: deviceId
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Local storage temizle
      stopSessionHeartbeat();
      clearRememberedLogin();
      localStorage.removeItem('session_id');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  }
};

// ==================== SESSION HEARTBEAT ====================
let heartbeatInterval = null;
let heartbeatFailCount = 0;
const MAX_HEARTBEAT_FAILS = 3; // 3 başarısız deneme sonrası logout

const startSessionHeartbeat = () => {
  // Mevcut interval'ı temizle
  stopSessionHeartbeat();
  heartbeatFailCount = 0;
  
  // 3 dakikada bir heartbeat gönder
  heartbeatInterval = setInterval(async () => {
    try {
      const { sessionId, deviceId } = getSessionInfo();
      if (sessionId && deviceId) {
        const response = await apiClient.post('/session/heartbeat', {
          session_id: sessionId,
          device_id: deviceId
        });
        
        if (response.data?.success) {
          // Başarılı - fail sayacını sıfırla
          heartbeatFailCount = 0;
        } else {
          // Session bulunamadı - fail sayacını artır
          heartbeatFailCount++;
          console.warn(`Session heartbeat failed (${heartbeatFailCount}/${MAX_HEARTBEAT_FAILS})`);
          
          if (heartbeatFailCount >= MAX_HEARTBEAT_FAILS) {
            // Önce silent re-auth ile session'ı yenilemeyi dene (kullanıcıyı atma)
            const newToken = await silentReauth();
            if (newToken) {
              heartbeatFailCount = 0;
              console.info('🔄 Heartbeat: silent re-auth ile session yenilendi');
              return;
            }
            console.warn('Session expired after multiple failures + silent re-auth failed, logging out...');
            authAPI.logout();
            window.location.href = '/login?expired=1';
          }
        }
      }
    } catch (error) {
      // Network hatası - tolere et
      heartbeatFailCount++;
      console.error(`Heartbeat network error (${heartbeatFailCount}/${MAX_HEARTBEAT_FAILS}):`, error.message);
      
      if (heartbeatFailCount >= MAX_HEARTBEAT_FAILS) {
        // Network hatalarında da silent re-auth dene — belki backend geri geldi
        const newToken = await silentReauth();
        if (newToken) {
          heartbeatFailCount = 0;
          console.info('🔄 Heartbeat: silent re-auth ile session yenilendi (network recovery)');
          return;
        }
        console.warn('Too many heartbeat failures, logging out...');
        authAPI.logout();
        window.location.href = '/login?expired=1';
      }
    }
  }, 3 * 60 * 1000); // 3 dakika
  
  console.log('🫀 Session heartbeat started (3 min interval, 3 fail tolerance)');
};

const stopSessionHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
    console.log('💔 Session heartbeat stopped');
  }
};

// Sayfa yüklendiğinde mevcut session varsa heartbeat başlat
if (localStorage.getItem('session_id') && localStorage.getItem('user')) {
  startSessionHeartbeat();
}

// Product API
export const productAPI = {
  /**
   * Barkod ile ürün getir (Proxy üzerinden)
   */
  getByBarcode: async (barcode) => {
    const logData = {
      user_code: '',
      username: '',
      company_name: '',
      request_type: 'product_search',
      request_data: { barcode },
      response_status: '',
      response_data: {},
      error: null,
      device_info: navigator.userAgent
    };

    try {
      // companyName ve warehouseCode'u localStorage'dan al
      const userStr = localStorage.getItem('user');
      let companyName = 'Bogazici';
      let warehouseCode = 'M02'; // Default
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          logData.user_code = user.personelKodu || user.username;
          logData.username = user.username;
          logData.company_name = user.companyName;
          
          if (user.companyName) {
            companyName = user.companyName;
          }
          if (user.warehouseCode) {
            warehouseCode = user.warehouseCode;
          }
        } catch (err) {
          console.error('Error parsing user:', err);
        }
      }
      
      console.log('🔍 Ürün Arama Başladı');
      console.log('🌐 Backend URL:', getBackendURL());
      console.log('📍 Window origin:', window.location.origin);
      
      // Tam request URL'ini kaydet
      const fullUrl = `${getBackendURL()}/api/proxy/product/${barcode}?companyName=${companyName}&warehousecode=${warehouseCode}`;
      
      // External API URL'ini de kaydet (IP olmadan - sadece path)
      const externalApiUrl = `/api/Integrator/item/${barcode}?warehousecode=${warehouseCode}&companyName=${companyName}`;
      
      logData.request_data = {
        barcode: barcode,
        companyName: companyName,
        warehouseCode: warehouseCode,
        fullUrl: fullUrl,
        externalApiUrl: externalApiUrl,
        method: 'GET',
        timestamp: new Date().toISOString()
      };
      
      console.log('📤 Request Data:', logData.request_data);
      
      // Proxy endpoint: /proxy/product/{barcode}?companyName={companyName}&warehousecode={warehouseCode}
      const response = await apiClient.get(`/proxy/product/${barcode}`, {
        params: { 
          companyName,
          warehousecode: warehouseCode
        }
      });
      
      console.log('📥 Response status:', response.status);
      console.log('📦 Response data type:', typeof response.data);
      console.log('📦 Response data:', response.data);
      
      logData.response_status = response.status;
      
      // Response kontrolü - boş veya null ise hata fırlat
      if (!response || !response.data) {
        console.error('❌ Response veya data boş');
        logData.error = 'API\'den boş yanıt alındı';
        throw new Error('API\'den boş yanıt alındı');
      }
      
      const apiData = response.data;
      console.log('✅ API Data alındı, type:', typeof apiData, 'isArray:', Array.isArray(apiData));
      
      // API data kontrolü - null, undefined, boş array
      if (!apiData || apiData === null || apiData === undefined) {
        console.error('❌ API data null veya undefined');
        logData.error = 'API\'den boş yanıt alındı';
        throw new Error('API\'den boş yanıt alındı');
      }
      
      if (Array.isArray(apiData) && apiData.length === 0) {
        console.error('❌ API data boş array');
        logData.error = 'Ürün bulunamadı';
        throw new Error('Ürün bulunamadı');
      }
      
      // Array formatında geliyorsa
      if (Array.isArray(apiData) && apiData.length > 0) {
        console.log('✅ Array ve dolu, ilk item:', apiData[0]);
        const item = apiData[0];
        
        // Null/undefined kontrolü
        if (!item || item === null || item === undefined) {
          console.error('❌ Item null veya undefined');
          logData.error = 'Ürün verisi boş';
          throw new Error('Ürün verisi boş');
        }
        
        console.log('✅ Item var, parsing başlıyor...');
        
        // WarehouseList parse - MUTLAKA TRY-CATCH İÇİNDE
        let warehouses = [];
        try {
          console.log('🔄 WarehouseList parsing...');
          if (item.WarehouseList !== null && item.WarehouseList !== undefined && item.WarehouseList !== '') {
            const warehouseData = typeof item.WarehouseList === 'string' 
              ? JSON.parse(item.WarehouseList)
              : item.WarehouseList;
            
            if (Array.isArray(warehouseData) && warehouseData.length > 0) {
              warehouses = warehouseData.map(w => ({
                name: (w && w.WarehouseDescription) || (w && w.name) || '',
                stock: (w && (w.Envanter || w.stock)) || 0,
                color: (w && w.ColorDescription) || '',
                size: (w && w.ItemDim1Code) || ''
              }));
            }
          }
          console.log('✅ WarehouseList parsed:', warehouses.length, 'items');
        } catch (err) {
          console.error('⚠️ WarehouseList parse error (devam ediliyor):', err);
          warehouses = [];
        }
        
        // ItemDetailList parse - MUTLAKA TRY-CATCH İÇİNDE
        let itemDetailList = [];
        try {
          console.log('🔄 ItemDetailList parsing...');
          if (item.ItemDetailList !== null && item.ItemDetailList !== undefined && item.ItemDetailList !== '') {
            if (typeof item.ItemDetailList === 'string' && item.ItemDetailList.trim() !== '') {
              const parsed = JSON.parse(item.ItemDetailList);
              if (Array.isArray(parsed)) {
                itemDetailList = parsed;
              }
            } else if (Array.isArray(item.ItemDetailList)) {
              itemDetailList = item.ItemDetailList;
            }
          }
          console.log('✅ ItemDetailList parsed:', itemDetailList.length, 'items');
        } catch (e) {
          console.error('⚠️ ItemDetailList parse error (devam ediliyor):', e);
          itemDetailList = [];
        }

        console.log('✅ Parsing tamamlandı, result data oluşturuluyor...');

        const resultData = {
          success: true,
          data: {
            code: (item && item.Itemcode) || (item && item.code) || 'N/A',
            name: (item && item.ItemDescription) || (item && item.name) || 'İsimsiz Ürün',
            // HTTP linklerini backend proxy üzerinden HTTPS'e çevir
            image: (item && item.Photo) ? 
              `${getBackendURL()}/api/proxy/image?url=${encodeURIComponent(item.Photo)}` : 
              null,
            price: parseFloat((item && (item.Price || item.price)) || 0),
            indirimliPrice: item?.IndirimliPrice != null ? parseFloat(item.IndirimliPrice) : null,
            stock: parseInt((item && (item.Envanter || item.stock)) || 0),
            warehouses: warehouses || [],
            itemDetailList: itemDetailList || [],
            color: (item && item.ColorDescription) || '',
            size: (item && item.ItemDim1Code) || ''
          },
          message: "Product found"
        };
        
        console.log('✅ Result data hazır:', resultData);
        logData.response_data = resultData;
        
        // Loglama yap
        try {
          console.log('💾 Log kaydediliyor...');
          await apiClient.post('/logs/api-request', logData);
          console.log('✅ Log kaydedildi');
        } catch (logError) {
          console.warn('⚠️ Log kaydedilemedi:', logError);
        }
        
        console.log('✅ Ürün başarıyla döndürülüyor');
        return resultData;
      }
      
      console.error('❌ Array değil veya boş');
      logData.error = 'Ürün bulunamadı';
      throw new Error('Ürün bulunamadı');
      
    } catch (error) {
      // Hata logla
      logData.error = error.message;
      logData.response_status = error.response?.status || 'error';
      
      try {
        await apiClient.post('/logs/api-request', logData);
        console.log('❌ Hata logu kaydedildi');
      } catch (logError) {
        console.warn('⚠️ Hata logu kaydedilemedi:', logError);
      }
      
      // Detaylı hata mesajı
      let errorMessage = 'Ürün bilgisi alınamadı';
      
      if (error.message === 'Ürün bulunamadı') {
        errorMessage = 'Bu barkod için ürün bulunamadı';
      } else if (error.message === 'API\'den boş yanıt alındı') {
        errorMessage = 'API bağlantı hatası. Lütfen tekrar deneyin.';
      } else if (error.response) {
        // API'den gelen hata
        errorMessage = error.response.data?.detail || error.response.statusText || errorMessage;
      } else if (error.message) {
        // Diğer hatalar
        errorMessage = error.message;
      }
      
      console.error('Product fetch error:', errorMessage, error);
      
      throw {
        message: errorMessage,
        error: 'PRODUCT_ERROR'
      };
    }
  },
  
  // Alias for getByBarcode - same function with different name
  getProduct: async (barcode, companyName) => {
    // companyName parametresi varsa localStorage'a kaydet (opsiyonel kullanım için)
    if (companyName) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          user.companyName = companyName;
          localStorage.setItem('user', JSON.stringify(user));
        } catch (err) {
          console.error('Error updating user companyName:', err);
        }
      }
    }
    
    // getByBarcode'u çağır (aynı fonksiyon)
    return productAPI.getByBarcode(barcode);
  }
};

// Reports API
export const reportsAPI = {
  /**
   * Warehouse listesini getir
   */
  getWarehouseList: async () => {
    try {
      // companyName'i localStorage'dan al
      const userStr = localStorage.getItem('user');
      let companyName = 'Bogazici';
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.companyName) {
            companyName = user.companyName;
          }
        } catch (err) {
          console.error('Error parsing user:', err);
        }
      }
      
      console.log('Warehouse List API Request:', { companyName });
      
      // External API endpoint'ini kullan - eski format
      const response = await apiClient.get('/Reports/warehouse-list', {
        params: { companyName }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'Depo listesi alınamadı',
        error: 'WAREHOUSE_LIST_ERROR'
      };
    }
  },

  /**
   * Ürün satış analizi raporu getir
   */
  getProductSalesAnalysis: async (startDate, endDate, warehouseCode, itemdim, warehouseCodes) => {
    try {
      const userStr = localStorage.getItem('user');
      let companyName = 'Bogazici';
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.companyName) companyName = user.companyName;
        } catch (err) { console.error('Error parsing user:', err); }
      }

      const params = { startDate, endDate, companyName };
      if (warehouseCodes) params.warehouseCodes = warehouseCodes;
      else if (warehouseCode) params.warehouseCode = warehouseCode;
      if (itemdim !== null && itemdim !== undefined) params.Itemdim = itemdim;

      const response = await apiClient.get('/Reports/urun-satis-analizi', { params });
      return { success: true, data: response.data };
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'Rapor alınamadı',
        error: 'REPORT_ERROR'
      };
    }
  },

  /**
   * Günlük satış raporu getir
   */
  getDailySalesReport: async (date, companyName) => {
    try {
      const response = await apiClient.get('/Reports/magaza-gunluk-satis', {
        params: { 
          date,
          companyName
        },
        timeout: 120000
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'Rapor alınamadı',
        error: 'REPORT_ERROR'
      };
    }
  },

  /**
   * Ürün envanter raporu getir
   */
  getInventoryReport: async (warehouseCode, companyName) => {
    try {
      const response = await apiClient.get('/Reports/urun-envanter', {
        params: { 
          warehouseCode,
          companyName
        },
        timeout: 120000
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'Rapor alınamadı',
        error: 'REPORT_ERROR'
      };
    }
  },

  /**
   * Ürün karlılık raporu getir
   */
  getProfitabilityReport: async (startDate, endDate, warehouseCode, itemdim, warehouseCodes) => {
    try {
      const userStr = localStorage.getItem('user');
      let companyName = 'Bogazici';
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.companyName) companyName = user.companyName;
        } catch (err) { console.error('Error parsing user:', err); }
      }

      const params = { startDate, endDate, companyName };
      if (warehouseCodes) params.warehouseCodes = warehouseCodes;
      else if (warehouseCode) params.warehouseCode = warehouseCode;
      if (itemdim !== null && itemdim !== undefined) params.Itemdim = itemdim;

      const response = await apiClient.get('/Reports/urun-satis-analizi-kar', { params });
      return { success: true, data: response.data };
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'Rapor alınamadı',
        error: 'REPORT_ERROR'
      };
    }
  },

  getMonthlyBestSellers: async (companyName, startDate, endDate, itemdim, warehouseCode, warehouseCodes) => {
    try {
      const params = { companyName };
      if (startDate) params.StartDate = startDate;
      if (endDate) params.EndDate = endDate;
      if (itemdim !== null && itemdim !== undefined) params.Itemdim = itemdim;
      if (warehouseCodes) params.warehouseCodes = warehouseCodes;
      else if (warehouseCode) params.warehouseCode = warehouseCode;

      const response = await apiClient.get('/Reports/en-cok-satan-aylik', { params, timeout: 180000 });
      return { success: true, data: response.data };
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'Rapor alınamadı',
        error: 'REPORT_ERROR'
      };
    }
  },

  getWeeklyBestSellers: async (companyName, startDate, endDate, itemdim, warehouseCode, warehouseCodes) => {
    try {
      const params = { companyName };
      if (startDate) params.StartDate = startDate;
      if (endDate) params.EndDate = endDate;
      if (itemdim !== null && itemdim !== undefined) params.Itemdim = itemdim;
      if (warehouseCodes) params.warehouseCodes = warehouseCodes;
      else if (warehouseCode) params.warehouseCode = warehouseCode;

      const response = await apiClient.get('/Reports/en-cok-satan-haftalik', { params, timeout: 180000 });
      return { success: true, data: response.data };
    } catch (error) {
      throw {
        message: error.response?.data?.detail || error.message || 'Rapor alınamadı',
        error: 'REPORT_ERROR'
      };
    }
  }
};

export default apiClient;
